import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = "https://safesheatlas.com/api";

const UserReportForm = ({ destinationId, onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    report_type: 'general',
    description: '',
    location_detail: '',
    reporter_email: '',
    time_of_incident: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/reports/submit`, {
        destination_id: destinationId,
        ...formData
      });
      
      toast.success('Report submitted! It will be reviewed by our team.');
      
      // Reset form
      setFormData({
        report_type: 'general',
        description: '',
        location_detail: '',
        reporter_email: '',
        time_of_incident: ''
      });
      
      if (onSubmitSuccess) onSubmitSuccess();
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="user-report-form">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <p className="text-sm text-blue-800">
          Your report helps keep other women safe. All submissions are reviewed before being published.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Report Type *</label>
        <Select
          value={formData.report_type}
          onValueChange={(value) => setFormData({ ...formData, report_type: value })}
        >
          <SelectTrigger data-testid="report-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="general">General Safety Observation</SelectItem>
            <SelectItem value="harassment">Harassment</SelectItem>
            <SelectItem value="theft">Theft/Robbery</SelectItem>
            <SelectItem value="assault">Assault</SelectItem>
            <SelectItem value="positive">Positive Experience</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Description *</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          required
          rows={5}
          placeholder="Please describe what happened or what you observed..."
          className="resize-none"
          data-testid="report-description"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">
          <MapPin className="w-4 h-4 inline mr-1" />
          Location Details
        </label>
        <Input
          value={formData.location_detail}
          onChange={(e) => setFormData({ ...formData, location_detail: e.target.value })}
          placeholder="E.g., Near Central Station, Main Street..."
          data-testid="report-location"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Time of Incident</label>
        <Input
          type="datetime-local"
          value={formData.time_of_incident}
          onChange={(e) => setFormData({ ...formData, time_of_incident: e.target.value })}
          data-testid="report-time"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Your Email (Optional)</label>
        <Input
          type="email"
          value={formData.reporter_email}
          onChange={(e) => setFormData({ ...formData, reporter_email: e.target.value })}
          placeholder="For follow-up questions only (not published)"
          data-testid="report-email"
        />
        <p className="text-xs text-gray-500 mt-1">Your email will never be published or shared</p>
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6"
        data-testid="submit-report-button"
      >
        {submitting ? 'Submitting...' : 'Submit Report'}
      </Button>
    </form>
  );
};

export default UserReportForm;